import type { SearchContext } from '@shared/types/types.ts';
import {
    type BrandItem,
    type ProductItem,
    type TileListItem,
    isProductItem,
} from '@tilelist/domain/composition/TileListitem.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';
import { getBrandTileCampaign, isBrandTileTreatment } from '@tilelist/domain/feature-visibility/BrandTile.ts';
import { hasProductComparisonBanner } from '@tilelist/domain/feature-visibility/ComparisonBanner.ts';
import { insertMASBannerTargets } from '@tilelist/domain/feature-visibility/MASBanner.ts';

export const SDA_BTF_POSITION_WITHOUT_PCB = 12;
export const SDA_BTF_POSITION_WITH_PCB = 18;
export const SDA_BTF_MINIMUM_TILES = 20;

/**
 *
 *
 *
 */
export function findInsertIndexAfterNthProduct(items: TileListItem[], n: number): number {
    let productCount = 0;
    const nthProductIndex = items.findIndex((item) => {
        if (isProductItem(item)) productCount++;
        return productCount === n;
    });
    return nthProductIndex >= 0 ? nthProductIndex + 1 : items.length;
}

/**
 *
 *
 *
 *
 */
export function insertSDABillboard(
    tileListItems: TileListItem[],
    searchContext?: SearchContext,
    onex?: OnEx,
): TileListItem[] {
    const productItemCount = tileListItems.filter(isProductItem).length;
    if (productItemCount <= SDA_BTF_MINIMUM_TILES) {
        return tileListItems;
    }

    const hasPCB = hasProductComparisonBanner(tileListItems);
    const targetProductPosition = hasPCB ? SDA_BTF_POSITION_WITH_PCB : SDA_BTF_POSITION_WITHOUT_PCB;
    const campaign = getBrandTileCampaign(searchContext);
    const isBrandTilePage = isBrandTileTreatment(searchContext, onex);

    if (isBrandTilePage && campaign) {
        /*                                                                                 */
        /*                                                                */
        const tileListItemsWithoutMAS = removeMASBannerTargets(tileListItems);
        const brandTileInsertIndex = findInsertIndexAfterNthProduct(tileListItemsWithoutMAS, targetProductPosition);
        const replacedProductIndex = findNthProductIndex(tileListItemsWithoutMAS, targetProductPosition + 1);
        const replacedProduct =
            replacedProductIndex === -1 || !isProductItem(tileListItemsWithoutMAS[replacedProductIndex])
                ? undefined
                : tileListItemsWithoutMAS[replacedProductIndex];

        const brandTile: BrandItem = {
            type: 'BrandTile',
            product: { id: 'brand-tile' },
            campaign,
            localListPosition: replacedProduct?.localListPosition ?? targetProductPosition + 1,
            actualListPosition:
                replacedProduct?.actualListPosition ??
                getInsertActualListPosition(tileListItemsWithoutMAS, brandTileInsertIndex),
        };

        const brandTileItems = insertBrandTile(
            tileListItemsWithoutMAS,
            replacedProductIndex === -1 ? brandTileInsertIndex : replacedProductIndex,
            brandTile,
        );

        const finalItems = insertMASBannerTargets(brandTileItems);

        /*                                                                          */
        /*                                                                   */
        const sdaInsertIndex = findInsertIndexAfterNthProduct(finalItems, targetProductPosition);
        const hiddenSdaItem = {
            type: 'SponsoredDisplayAd' as const,
            searchContext: getBtfSearchContext(searchContext),
            hidden: true as const,
        };

        return [...finalItems.slice(0, sdaInsertIndex), hiddenSdaItem, ...finalItems.slice(sdaInsertIndex)];
    }

    const insertIndex = findInsertIndexAfterNthProduct(tileListItems, targetProductPosition);
    const sdaItem = {
        type: 'SponsoredDisplayAd' as const,
        searchContext: getBtfSearchContext(searchContext),
    };

    return [...tileListItems.slice(0, insertIndex), sdaItem, ...tileListItems.slice(insertIndex)];
}

function findNthProductIndex(items: TileListItem[], n: number): number {
    let productCount = 0;
    for (let i = 0; i < items.length; i++) {
        if (isProductItem(items[i]) && ++productCount === n) {
            return i;
        }
    }

    return -1;
}

function insertBrandTile(
    tileListItems: TileListItem[],
    replacementIndex: number,
    brandTile: Extract<TileListItem, { type: 'BrandTile' }>,
): TileListItem[] {
    const itemsBefore = tileListItems.slice(0, replacementIndex);
    const itemsAfter = tileListItems.slice(replacementIndex);

    const lastPreSimilarProductIndex = findLastPreSimilarProductIndex(itemsAfter);
    const trimmedAfter =
        lastPreSimilarProductIndex === -1
            ? itemsAfter
            : itemsAfter.filter((_, index) => index !== lastPreSimilarProductIndex);

    const shiftedAfter = trimmedAfter.map((item) => shiftProductPositions(item, brandTile.localListPosition));

    return [...itemsBefore, brandTile, ...shiftedAfter];
}

function removeMASBannerTargets(tileListItems: TileListItem[]): TileListItem[] {
    return tileListItems.filter((item) => item.type !== 'BT');
}

function getInsertActualListPosition(tileListItems: TileListItem[], insertIndex: number): number {
    for (let index = insertIndex - 1; index >= 0; index--) {
        const item = tileListItems[index];

        if (isProductItem(item)) {
            return item.actualListPosition + 1;
        }
    }

    return 1;
}

function findLastPreSimilarProductIndex(items: TileListItem[]): number {
    const similarHeaderIndex = items.findIndex((item) => item.type === 'SPH');
    const searchEnd = similarHeaderIndex === -1 ? items.length - 1 : similarHeaderIndex - 1;

    for (let index = searchEnd; index >= 0; index--) {
        if (isProductItem(items[index])) {
            return index;
        }
    }

    return -1;
}

function shiftProductPositions(item: TileListItem, fromLocalListPosition: number): TileListItem {
    if (!isProductItem(item) || item.localListPosition < fromLocalListPosition) {
        return item;
    }

    return {
        ...item,
        localListPosition: item.localListPosition + 1,
        actualListPosition: item.actualListPosition + 1,
    } satisfies ProductItem;
}

/**
 *
 *
 *
 */
export function getBtfSearchContext(searchContext?: SearchContext): SearchContext | undefined {
    if (!searchContext) return undefined;
    const { searchTerm, assortment, categoryPath, pbk, explicit, implicit, topNImplicit } = searchContext;
    const hasContextFields = [searchTerm, assortment, categoryPath, pbk, explicit, implicit, topNImplicit].some(
        (v) => v !== undefined,
    );
    return hasContextFields ? searchContext : undefined;
}
